// client/components/ui/CollapsibleBlock.jsx
import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import AppButton from "./AppButton.jsx";
import { segmentTabChipButtonClassName } from "../../design/tokens.js";

/** Meta rechts: eine Zeile, ellipsis bei Platzmangel; voller Text per nativem `title` (Kalkulation & Co.) */
const headerInfoTextClassName =
  "ml-auto min-w-0 max-w-[min(100%,18rem)] shrink text-xs tabular-nums text-right align-middle inline-block whitespace-nowrap overflow-hidden text-ellipsis !text-blue-200/90";

const headerInfoChipShellClassName =
  "ml-auto min-w-0 shrink inline-flex max-w-[min(100%,18rem)] items-center justify-end";

function HeaderInfoSlot({
  info,
  responsiveClassName,
  headerInfoTitle,
  headerInfoChip,
  headerInfoChipTone,
}) {
  if (info == null || info === false) return null;
  const tooltip =
    (typeof headerInfoTitle === "string" && headerInfoTitle.trim()) ||
    (typeof info === "string" && info.trim()) ||
    undefined;
  if (headerInfoChip) {
    const toneCls =
      headerInfoChipTone === "success" ? "seg-btn-active-success" : "seg-btn-active-primary";
    return (
      <span className={`${headerInfoChipShellClassName} ${responsiveClassName}`} title={tooltip}>
        <span
          className={`inline-flex max-w-full items-center justify-center ${segmentTabChipButtonClassName} font-semibold tabular-nums whitespace-nowrap ${toneCls}`}
        >
          {info}
        </span>
      </span>
    );
  }
  return (
    <span className={`${headerInfoTextClassName} ${responsiveClassName}`} title={tooltip}>
      {info}
    </span>
  );
}

/**
 * Zentrale CollapsibleBlock-Komponente für alle Block-Komponenten
 * 
 * @param {Object} props
 * @param {string} props.title - Titel des Blocks (kann Emoji enthalten)
 * @param {React.ReactNode} props.children - Inhalt des Blocks
 * @param {boolean} props.defaultOpen - Standardmäßig geöffnet
 * @param {boolean} [props.open] - Kontrollierter Offen-Status (optional)
 * @param {function} [props.onOpenChange] - Callback bei open-Änderung (kontrolliert)
 * @param {boolean} props.embedded - Immer geöffnet (kein Toggle)
 * @param {string|React.ReactNode} props.headerInfo - Info rechts im Header
 * @param {string|React.ReactNode} props.headerInfoMobile - Info für Mobile (optional)
 * @param {boolean} [props.headerInfoChip] - Segment-Chip wie „Meine Aufgaben“ (grün/blau)
 * @param {"primary"|"success"} [props.headerInfoChipTone] - `success` = erledigt-Vollständigkeit (grün), sonst blau
 * @param {string} props.headerInfoTitle - Optional: langer Text nur für natives `title` (Tooltip), wenn die Anzeige gekürzt ist
 * @param {string} props.panelId - ID für aria-controls (auto-generiert wenn nicht gesetzt)
 * @param {string} props.className - Zusätzliche CSS-Klassen für section
 * @param {string} props.headerClassName - Zusätzliche CSS-Klassen für Header-Button
 * @param {string} props.contentClassName - Zusätzliche CSS-Klassen für Content
 * @param {boolean} props.showBorderBottom - Border-bottom im Header-Button
 * @param {boolean} props.useChevronUp - ChevronUp statt ChevronDown wenn offen
 * @param {function} props.onToggle - Callback wenn Block geöffnet/geschlossen wird
 * @param {React.ReactNode} props.headerActions - Zusätzliche Actions im Header (rechts)
 * @param {function} props.onHeaderClick - Bei embedded: Klick auf den Header (öffnet z.B. Modal) – statt separatem Öffnen-Button
 * @param {boolean} [props.compact] - Kompaktere Kopfzeile (wie Service/Projekt-Detail)
 * @param {boolean} [props.raiseOnFocus] - Bei Fokus z-Index anheben (Dropdowns über Nachbarn)
 */
export default function CollapsibleBlock({
  title,
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  embedded = false,
  compact = false,
  headerInfo,
  headerInfoMobile,
  headerInfoChip = false,
  headerInfoChipTone = "primary",
  headerInfoTitle,
  panelId,
  className = "",
  headerClassName = "",
  contentClassName = "",
  showBorderBottom = false,
  useChevronUp = false,
  onToggle,
  headerActions,
  onHeaderClick,
  persistKey,
  raiseOnFocus = true,
}) {
  const isControlled = controlledOpen !== undefined && typeof onOpenChange === "function";
  const [internalOpen, setInternalOpen] = useState(() => {
    if (embedded) return true;
    // Aus localStorage wiederherstellen (wenn persistKey vorhanden)
    if (persistKey) {
      try {
        const saved = localStorage.getItem(persistKey);
        if (saved !== null) return JSON.parse(saved);
      } catch {}
    }
    return defaultOpen;
  });
  const open = isControlled ? Boolean(controlledOpen) : internalOpen;
  
  // Auto-generate panelId if not provided
  const id = panelId || `collapsible-panel-${Math.random().toString(36).slice(2, 9)}`;
  
  // Wenn embedded, immer offen halten
  useEffect(() => {
    if (embedded && !open) {
      if (isControlled) onOpenChange?.(true);
      else setInternalOpen(true);
    }
  }, [embedded, open, isControlled, onOpenChange]);
  
  // Persist open state to localStorage
  useEffect(() => {
    if (persistKey && !embedded && !isControlled) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(open));
      } catch {}
    }
  }, [persistKey, open, embedded, isControlled]);
  
  const handleToggle = () => {
    if (embedded) return;
    const newOpen = !open;
    if (isControlled) onOpenChange?.(newOpen);
    else setInternalOpen(newOpen);
    onToggle?.(newOpen);
  };
  
  const ChevronIcon = useChevronUp 
    ? (open ? ChevronUp : ChevronDown)
    : (open ? ChevronDown : ChevronRight);

  const headerPad = compact ? "px-3 sm:px-4 py-1.5 sm:py-2" : "px-3 sm:px-4 py-2 md:py-2.5";
  const headerMinH = compact ? "min-h-[44px] md:min-h-0" : "min-h-[52px] md:min-h-11";
  const titleClassName = compact
    ? "text-sm sm:text-base font-semibold truncate whitespace-nowrap text-primary min-w-0 flex-1 text-left"
    : "text-base md:text-lg font-semibold truncate whitespace-nowrap text-primary min-w-0 flex-1 text-left";
  const chevronClassName = compact ? "w-3.5 h-3.5 shrink-0 text-muted" : "w-4 h-4 shrink-0 text-muted";
  const toggleBtnSize = compact ? "sm" : "md";
  const hasContent =
    children != null &&
    children !== false &&
    !(Array.isArray(children) && children.length === 0);

  return (
    <section className={`relative overflow-visible z-0 ${raiseOnFocus ? "focus-within:z-[1200]" : ""} rounded-2xl border border-default bg-surface ${className}`}>
      {embedded ? (
        (onHeaderClick && typeof onHeaderClick === "function") ? (
          <AppButton
            type="button"
            variant="ghost"
            onClick={onHeaderClick}
            size={toggleBtnSize}
            className={`w-full !justify-start flex items-center gap-2 ${headerPad} ${headerMinH} text-left ${
              showBorderBottom ? "border-b border-default" : ""
            } ${headerClassName}`}
            aria-expanded={false}
            aria-controls={id}
          >
            <ChevronRight className={chevronClassName} />
            <span className={titleClassName}>
              {title}
            </span>
            {headerInfo ? (
              <HeaderInfoSlot
                info={headerInfo}
                responsiveClassName="hidden md:inline"
                headerInfoTitle={headerInfoTitle}
                headerInfoChip={headerInfoChip}
                headerInfoChipTone={headerInfoChipTone}
              />
            ) : null}
            {(headerInfoMobile !== undefined ? headerInfoMobile : headerInfo) ? (
              <HeaderInfoSlot
                info={headerInfoMobile !== undefined ? headerInfoMobile : headerInfo}
                responsiveClassName="md:hidden"
                headerInfoTitle={headerInfoTitle}
                headerInfoChip={headerInfoChip}
                headerInfoChipTone={headerInfoChipTone}
              />
            ) : null}
            {headerActions && <div className="ml-2 shrink-0">{headerActions}</div>}
          </AppButton>
        ) : (
        <div
          className={`w-full flex items-center justify-start gap-2 ${headerPad} ${
            showBorderBottom ? "border-b border-default" : ""
          } ${headerClassName}`}
        >
          <span className={titleClassName}>
            {title}
          </span>
          {headerInfo ? (
            <HeaderInfoSlot
              info={headerInfo}
              responsiveClassName="hidden md:inline"
              headerInfoTitle={headerInfoTitle}
              headerInfoChip={headerInfoChip}
              headerInfoChipTone={headerInfoChipTone}
            />
          ) : null}
          {(headerInfoMobile !== undefined ? headerInfoMobile : headerInfo) ? (
            <HeaderInfoSlot
              info={headerInfoMobile !== undefined ? headerInfoMobile : headerInfo}
              responsiveClassName="md:hidden"
              headerInfoTitle={headerInfoTitle}
              headerInfoChip={headerInfoChip}
              headerInfoChipTone={headerInfoChipTone}
            />
          ) : null}
          {headerActions && <div className="ml-2 shrink-0">{headerActions}</div>}
        </div>
        )
      ) : (
        <AppButton
          type="button"
          variant="ghost"
          size={toggleBtnSize}
          onClick={handleToggle}
          className={`w-full !justify-start flex items-center gap-2 ${headerPad} ${headerMinH} text-left ${
            showBorderBottom ? "border-b border-default" : ""
          } ${headerClassName}`}
          aria-expanded={open}
          aria-controls={id}
        >
          <ChevronIcon className={chevronClassName} />
          <span className={titleClassName}>
            {title}
          </span>
          {headerInfo ? (
            <HeaderInfoSlot
              info={headerInfo}
              responsiveClassName="hidden md:inline"
              headerInfoTitle={headerInfoTitle}
              headerInfoChip={headerInfoChip}
              headerInfoChipTone={headerInfoChipTone}
            />
          ) : null}
          {(headerInfoMobile !== undefined ? headerInfoMobile : headerInfo) ? (
            <HeaderInfoSlot
              info={headerInfoMobile !== undefined ? headerInfoMobile : headerInfo}
              responsiveClassName="md:hidden"
              headerInfoTitle={headerInfoTitle}
              headerInfoChip={headerInfoChip}
              headerInfoChipTone={headerInfoChipTone}
            />
          ) : null}
          {headerActions && <div className="ml-2 shrink-0">{headerActions}</div>}
        </AppButton>
      )}
      
      {(embedded || open) && hasContent && (
        <div id={id} className={contentClassName}>
          {children}
        </div>
      )}
    </section>
  );
}
